import {useTranslation} from "react-i18next";
import React from "react";
import {CouponContext, StripePrice} from "../SubscriptionDetail";
import SinglePriceCard from "./SinglePriceCard";

const PlanListBlock = ({ prices, onSelect, selectedId, isPriceSingle, onPlanPressed }: {
    prices: StripePrice[],
    onSelect: (id: string) => void,
    onPlanPressed: () => void,
    selectedId: string,
    isPriceSingle: boolean
}) => {
    const { t } = useTranslation();
    const coupon = React.useContext(CouponContext);
    // Determine which price should be marked as most popular (default metadata takes precedence)
    const defaultPopularPriceId = React.useMemo(() => {
        const source = prices || [];
        const candidate = source.find(p => {
            const flag = (p.metadata && (p.metadata.default ?? p.metadata["default"])) as unknown;
            if (flag === true) return true;
            if (typeof flag === 'string') {
                const normalized = flag.trim().toLowerCase();
                return normalized === 'true' || normalized === '1' || normalized === 'yes';
            }
            return false;
        });
        return candidate?.id || '';
    }, [prices]);

    if (!prices || prices.length === 0) return <div>{t('subscription.noPlansAvailable')}</div>;

    // If single price, render the special single price card
    if (isPriceSingle && prices.length === 1) {
        return (
            <div className="plan-list-block">
                <div className="plan-list-title">{t('subscription.yourPaymentPlan')}</div>
                <SinglePriceCard price={prices[0]} onSelect={onSelect} selectedId={selectedId} planButtonPressed={onPlanPressed}/>
            </div>
        );
    }

    // Sort prices from cheapest to most expensive
    const sortedPrices = [...prices].sort((a, b) => a.unit_amount - b.unit_amount);

    return (
        <div className="plan-list-block">
            <div className="plan-list-title">{t('subscription.chooseYourPlan')}</div>
            <div className="plan-list">
                {sortedPrices.map((plan, idx) => {
                    const interval = plan.recurring?.interval || 'month';
                    const intervalCount = plan.recurring?.interval_count || 1;
                    const priceNumber = plan.unit_amount / 100;
                    let discountedPrice = priceNumber;
                    if (coupon && coupon.percent_off) {
                        discountedPrice = priceNumber * (1 - coupon.percent_off / 100);
                    }
                    const pricePerInterval = discountedPrice.toLocaleString('en-US', { style: 'currency', currency: plan.currency.toUpperCase() });
                    const formattedDouble = priceNumber.toLocaleString('en-US', { style: 'currency', currency: plan.currency.toUpperCase() });

                    // Calculate daily price
                    const getDaysInInterval = (interval: string, intervalCount: number) => {
                        switch (interval) {
                            case 'day':
                                return intervalCount;
                            case 'week':
                                return intervalCount * 7;
                            case 'month':
                                return intervalCount * 30.44; // Average days per month
                            case 'year':
                                return intervalCount * 365.25; // Account for leap years
                            default:
                                return 30.44; // Default to monthly
                        }
                    };

                    const daysInInterval = getDaysInInterval(interval, intervalCount);
                    const dailyPrice = discountedPrice / daysInInterval;
                    const formattedDailyPrice = dailyPrice.toLocaleString('en-US', {
                        style: 'currency',
                        currency: plan.currency.toUpperCase(),
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });

                    // Get localized interval name
                    const getLocalizedInterval = (interval: string, count: number) => {
                        const key = count > 1 ? `subscription.intervals.${interval}s` : `subscription.intervals.${interval}`;
                        return t(key);
                    };

                    const localizedInterval = getLocalizedInterval(interval, intervalCount);
                    const name = plan.nickname || `${intervalCount > 1 ? intervalCount + ' ' : ''}${localizedInterval} ${t('subscription.plan')}`;
                    const isMostPopular = defaultPopularPriceId
                        ? plan.id === defaultPopularPriceId
                        : idx === sortedPrices.length - 1;
                    return (
                        <div
                            key={plan.id}
                            className={`plan-card${selectedId === plan.id ? ' plan-card-selected' : ''}`}
                            onClick={() => onSelect(plan.id)}
                        >
                            {isMostPopular && <div className="plan-card-badge">{t('subscription.mostPopular')}</div>}
                            <div className="plan-card-info">
                                <div className="plan-card-name">{name}</div>
                                <div className="plan-card-price">{formattedDailyPrice}/{t('subscription.day')}</div>
                                <div className="plan-card-prices">
                                    <span className="plan-card-old">{formattedDouble}</span>
                                    <span className="plan-card-new">{pricePerInterval}</span>
                                </div>
                            </div>
                            <div className="plan-card-radio">
                                <span className="plan-card-radio-outer">
                                    {selectedId === plan.id && <span className="plan-card-radio-inner" />}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PlanListBlock
